import { create } from 'zustand';

/**
 * The groups the chat list is divided into.
 *
 * Favourites cuts across the other two rather than sitting beside them: a
 * favourited group chat appears there instead of under Groups, not in both.
 * Showing a pinned conversation twice would make the panel's own count wrong.
 */
export type TeamsChatSection = 'favorites' | 'people' | 'groups';

const SECTIONS: readonly TeamsChatSection[] = ['favorites', 'people', 'groups'];

/**
 * Collapse state is remembered across reloads, not just across navigation.
 *
 * Sections are a decluttering tool — someone who collapses Groups is saying
 * "stop showing me these", and having that undone by a page refresh would make
 * the control feel broken. Written straight to localStorage rather than through
 * zustand's persist middleware, which nothing else in this app uses.
 */
const STORAGE_KEY = 'atlas.teamsChat.collapsedSections';

function loadCollapsed(): TeamsChatSection[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    // Anything unexpected in storage is discarded rather than trusted — this is
    // user-writable state, and a bad value must not be able to break the panel.
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v): v is TeamsChatSection =>
      SECTIONS.includes(v as TeamsChatSection),
    );
  } catch {
    return [];
  }
}

function saveCollapsed(sections: TeamsChatSection[]): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(sections));
  } catch {
    // Private browsing or a full quota. Losing the preference is not worth
    // breaking the click that triggered it.
  }
}

const WIDTH_STORAGE_KEY = 'atlas.teamsChat.panelWidth';

/** What the panel has always been, and what every child was laid out against. */
export const TEAMS_PANEL_DEFAULT_WIDTH = 320;

/**
 * Floor. Every child — the message bubbles, the composer with its send button
 * and character counter, the chat-list row with avatar + presence dot + preview
 * — was designed at 320, so there is no headroom to speak of. 280 is the point
 * at which those all still hold together with slightly tighter wrapping; below
 * it the composer's counter starts colliding with the button and list rows
 * truncate to uselessness. Narrower than this the honest answer is "close the
 * panel", not "shrink it".
 */
export const TEAMS_PANEL_MIN_WIDTH = 280;

/**
 * Ceiling, expressed as a share of the viewport rather than a pixel cap: the
 * thing being protected is the *app behind the panel*, and how much room that
 * needs scales with the window, not with a number we pick today. Half means the
 * page can never end up the smaller of the two — on a 1280px laptop that is a
 * 640px panel against 640px of app, which is already generous, and on an
 * ultrawide it lets the panel grow instead of being pinned to a cap that would
 * look arbitrary there.
 */
export const TEAMS_PANEL_MAX_VIEWPORT_FRACTION = 0.5;

/**
 * The upper bound for a given viewport. Never returns less than the minimum:
 * on a narrow-but-still-docked window half the viewport can fall below the
 * floor, and an inverted range would make the clamp nonsense.
 */
export function teamsPanelMaxWidth(viewportWidth: number): number {
  if (!Number.isFinite(viewportWidth)) return TEAMS_PANEL_DEFAULT_WIDTH;
  return Math.max(
    TEAMS_PANEL_MIN_WIDTH,
    Math.floor(viewportWidth * TEAMS_PANEL_MAX_VIEWPORT_FRACTION),
  );
}

/**
 * Pure, so it is the one place the bounds are enforced and the one thing worth
 * testing. Applied on every path — drag, keyboard, and read-back from storage —
 * because a persisted width is user-writable and the bounds can move between
 * releases, so a stored value is a suggestion, never a fact.
 */
export function clampPanelWidth(
  width: number,
  viewportWidth: number = typeof window === 'undefined'
    ? TEAMS_PANEL_DEFAULT_WIDTH * 2
    : window.innerWidth,
): number {
  if (!Number.isFinite(width)) return TEAMS_PANEL_DEFAULT_WIDTH;
  return Math.min(
    teamsPanelMaxWidth(viewportWidth),
    Math.max(TEAMS_PANEL_MIN_WIDTH, Math.round(width)),
  );
}

function loadPanelWidth(): number {
  try {
    const raw = window.localStorage.getItem(WIDTH_STORAGE_KEY);
    if (!raw) return TEAMS_PANEL_DEFAULT_WIDTH;
    // Same posture as the collapsed sections: parse defensively and re-clamp
    // rather than trusting what comes back.
    return clampPanelWidth(Number(raw));
  } catch {
    return TEAMS_PANEL_DEFAULT_WIDTH;
  }
}

function savePanelWidth(width: number): void {
  try {
    window.localStorage.setItem(WIDTH_STORAGE_KEY, String(width));
  } catch {
    // See saveCollapsed — a lost preference beats a thrown drag handler.
  }
}

/**
 * UI-only state for the persistent Teams chat sidebar.
 *
 * Deliberately holds NOTHING server-derived — chats and messages live in React
 * Query, which owns caching, polling, and invalidation. This store exists so the
 * drawer's open/closed state and the selected conversation survive route
 * changes, which is the whole point of a sidebar over a chat page.
 */
interface TeamsChatState {
  isOpen: boolean;
  /** The conversation being read, or null while showing the chat list. */
  activeChatId: string | null;
  /** Sections the user has collapsed. All sections start expanded. */
  collapsedSections: TeamsChatSection[];
  /**
   * Docked width in px. Only meaningful at md and up — below that the drawer
   * overlays at full width and this is ignored.
   */
  panelWidth: number;
  open: () => void;
  close: () => void;
  toggle: () => void;
  /** Select a conversation (drilling into the thread view). */
  setActiveChat: (chatId: string) => void;
  /** Return to the chat list. */
  clearActiveChat: () => void;
  toggleSection: (section: TeamsChatSection) => void;
  /** Commit a resize. Clamps against the current viewport and persists. */
  setPanelWidth: (width: number) => void;
}

export const useTeamsChatStore = create<TeamsChatState>((set) => ({
  isOpen: false,
  activeChatId: null,
  collapsedSections: loadCollapsed(),
  panelWidth: loadPanelWidth(),

  open: () => set({ isOpen: true }),
  close: () => set({ isOpen: false }),
  toggle: () => set((s) => ({ isOpen: !s.isOpen })),
  setActiveChat: (chatId) => set({ activeChatId: chatId }),
  clearActiveChat: () => set({ activeChatId: null }),

  toggleSection: (section) =>
    set((s) => {
      const next = s.collapsedSections.includes(section)
        ? s.collapsedSections.filter((v) => v !== section)
        : [...s.collapsedSections, section];
      saveCollapsed(next);
      return { collapsedSections: next };
    }),

  setPanelWidth: (width) =>
    set(() => {
      const next = clampPanelWidth(width);
      savePanelWidth(next);
      return { panelWidth: next };
    }),
}));
